import type { Patch, LfoShape, LfoTarget, OscillatorType } from "./types";
import { clonePatch } from "./defaultPatch";

// sfxr-style sound discovery, on the JSON level: `randomPatch()` rolls a whole
// new (musically bounded) sound; `mutatePatch()` nudges every knob of the
// current one a little. Both stay DOM-free and produce plain portable Patches.

const rnd = (a: number, b: number): number => a + Math.random() * (b - a);
const lrnd = (a: number, b: number): number => a * Math.pow(b / a, Math.random()); // log-uniform
const pick = <T>(xs: readonly T[]): T => xs[Math.floor(Math.random() * xs.length)];
const chance = (p: number): boolean => Math.random() < p;
const r2 = (v: number): number => Math.round(v * 100) / 100;

const OSC: readonly OscillatorType[] = ["sine", "square", "sawtooth", "triangle", "pulse", "noise"];
const SHAPES: readonly LfoShape[] = ["sine", "square", "sawtooth", "triangle"];
// Harmonic + the classic inharmonic (bell/clang) FM ratios.
const FM_RATIOS = [0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 5.1, 5.4, 7] as const;
const FIRST = ["Soft", "Glass", "Deep", "Neon", "Rusty", "Tiny", "Wild", "Cold", "Warm", "Odd"];
const SECOND = ["Blip", "Pluck", "Wave", "Bell", "Zap", "Buzz", "Drop", "Pad", "Tone", "Ping"];

/** Roll a brand-new sound within musical ranges (the 🎲 button). */
export function randomPatch(): Patch {
  const sustained = chance(0.45); // pad/lead vs pluck/hit
  const p: Patch = {
    version: 1,
    name: `${pick(FIRST)} ${pick(SECOND)}`,
    oscillator: {
      type: pick(OSC),
      detune: chance(0.3) ? Math.round(rnd(-25, 25)) : 0,
      pulseWidth: r2(rnd(0.15, 0.85)),
    },
    filter: {
      type: chance(0.75) ? "lowpass" : pick(["highpass", "bandpass", "notch"] as const),
      cutoff: Math.round(lrnd(250, 9000)),
      q: r2(lrnd(0.5, 12)),
    },
    ampEnv: {
      attack: sustained ? r2(lrnd(0.005, 0.4)) : r2(lrnd(0.001, 0.02)),
      decay: r2(lrnd(0.08, 1.0)),
      sustain: sustained ? r2(rnd(0.3, 0.9)) : 0,
      release: r2(lrnd(0.05, 0.8)),
    },
    lfo: { target: "off", shape: "sine", rate: 5, depth: 0 },
    masterGain: 0.6,
  };
  if (chance(0.35)) {
    const target = pick(["pitch", "amplitude", "filter"] as const) as LfoTarget;
    // Depth units depend on the target (cents / gain / Hz) — keep each subtle.
    const depth = target === "pitch" ? r2(rnd(4, 25)) : target === "amplitude" ? r2(rnd(0.1, 0.4)) : Math.round(rnd(30, 90));
    p.lfo = { target, shape: pick(SHAPES), rate: r2(lrnd(0.8, 12)), depth };
  }
  if (chance(0.45)) p.fm = { ratio: pick(FM_RATIOS), depth: r2(lrnd(0.4, 8)) };
  if (chance(0.3)) p.pitchEnv = { amount: Math.round(rnd(3, 36)), decay: r2(lrnd(0.02, 0.25)) };
  if (chance(0.35)) p.filterEnv = { amount: Math.round(rnd(400, 6000)), decay: r2(lrnd(0.05, 0.8)) };
  if (chance(0.35)) p.drive = r2(rnd(0.05, 0.5));
  return p;
}

/** Nudge every parameter of `src` a little (the Mutate button). */
export function mutatePatch(src: Patch, amount = 0.2): Patch {
  const p = clonePatch(src);
  delete p.xfadeTo; // mutate the sound itself, not a crossfade pair
  // Linear jog: move by a fraction of the param's range. Log jog: multiply.
  const jog = (v: number, lo: number, hi: number): number =>
    r2(Math.min(hi, Math.max(lo, v + (hi - lo) * rnd(-amount, amount) * 0.3)));
  const ljog = (v: number, lo: number, hi: number): number =>
    r2(Math.min(hi, Math.max(lo, v * Math.pow(4, rnd(-amount, amount)))));

  if (chance(0.08)) p.oscillator.type = pick(OSC);
  p.oscillator.detune = Math.round(jog(p.oscillator.detune, -1200, 1200));
  p.oscillator.pulseWidth = jog(p.oscillator.pulseWidth, 0.05, 0.95);
  p.filter.cutoff = Math.round(ljog(p.filter.cutoff, 40, 18000));
  p.filter.q = ljog(p.filter.q, 0.1, 24);
  p.ampEnv.attack = jog(p.ampEnv.attack, 0, 2);
  p.ampEnv.decay = jog(p.ampEnv.decay, 0.01, 2);
  p.ampEnv.sustain = jog(p.ampEnv.sustain, 0, 1);
  p.ampEnv.release = jog(p.ampEnv.release, 0.01, 3);
  if (p.lfo.target !== "off") {
    p.lfo.rate = ljog(p.lfo.rate, 0.1, 20);
    p.lfo.depth = ljog(p.lfo.depth, 0.01, 100);
  }
  if (p.drive) p.drive = jog(p.drive, 0, 1);
  if (p.pitchEnv?.amount) {
    p.pitchEnv.amount = Math.round(jog(p.pitchEnv.amount, 0, 48));
    p.pitchEnv.decay = jog(p.pitchEnv.decay, 0.01, 0.6);
  }
  if (p.fm?.depth) {
    p.fm.ratio = ljog(p.fm.ratio, 0.1, 8);
    p.fm.depth = ljog(p.fm.depth, 0, 12);
  }
  if (p.filterEnv?.amount) {
    p.filterEnv.amount = Math.round(jog(p.filterEnv.amount, 0, 10000));
    p.filterEnv.decay = jog(p.filterEnv.decay, 0.01, 1.5);
  }
  if (!p.name.endsWith("~")) p.name = `${p.name}~`.slice(0, 18);
  return p;
}
